import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users, Settings, Upload, Trash2, RefreshCw, Star, Home,
  FileSpreadsheet, BarChart3, Package, Heart, Plus, Film,
  Check, AlertCircle, Eye, Play, ChevronDown, ChevronUp,
  Download, Clock, Edit3, Info, Sparkles, Video, BookOpen,
  Zap, GripVertical, ArrowLeft, Activity, ToggleLeft, ToggleRight
} from "lucide-react";
import SystemHealthPanel from "../components/SystemHealthPanel";
import REVideoManager from "../components/REVideoManager";
import MemorialVideoManager from "../components/MemorialVideoManager";
import MemorialMomentsManager from "../components/MemorialMomentsManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// BackButton component (inline to avoid circular deps)
const BackButton = ({ to, label = "Back" }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => to ? navigate(to) : navigate(-1)}
      className="inline-flex items-center gap-2 text-zinc-400 hover:text-yellow-500 transition-colors mb-6 group"
      data-testid="back-button"
    >
      <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
      <span>{label}</span>
    </button>
  );
};

// StatusBadge component
const StatusBadge = ({ status }) => {
  const statusMap = {
    pending_payment: { label: "Pending Payment", class: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
    awaiting_upload: { label: "Awaiting Upload", class: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
    in_progress: { label: "In Progress", class: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
    review: { label: "Ready for Review", class: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" },
    completed: { label: "Completed", class: "bg-green-500/20 text-green-400 border-green-500/30" },
  };
  const s = statusMap[status] || { label: status, class: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" };
  return <span className={`px-3 py-1 rounded-full text-xs font-medium border ${s.class}`}>{s.label}</span>;
};

// RushBadge component
const RushBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full border border-red-500/30">
    <Zap size={12} /> RUSH
  </span>
);

// VideoBookBadge component
const VideoBookBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full border border-purple-500/30">
    <BookOpen size={12} /> VIDEO BOOK
  </span>
);

// VerticalBadge component
const VerticalBadge = ({ vertical }) => {
  const isRealEstate = vertical === "real_estate";
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
      isRealEstate 
        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
        : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    }`}>
      {isRealEstate ? <Home size={12} /> : <Heart size={12} />}
      {isRealEstate ? 'Real Estate' : 'Memorial'}
    </span>
  );
};

// Real Estate Showcase Uploader - Improved with Save/Cancel, Preview, Validation
const REShowcaseUploader = ({ reShowcase, setReShowcase, reShowcase2, setReShowcase2, API, BACKEND_URL }) => {
  // Hero Video State
  const [heroFile, setHeroFile] = useState(null);
  const [heroPreviewUrl, setHeroPreviewUrl] = useState(null);
  const [heroCaption, setHeroCaption] = useState("");
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroProgress, setHeroProgress] = useState(0);
  const [heroSaved, setHeroSaved] = useState(false);
  const [heroError, setHeroError] = useState("");
  const [heroStagedUrl, setHeroStagedUrl] = useState(null); // Staged URL before save
  const heroFileRef = useRef(null);

  // Bottom Video State  
  const [bottomFile, setBottomFile] = useState(null);
  const [bottomPreviewUrl, setBottomPreviewUrl] = useState(null);
  const [bottomCaption, setBottomCaption] = useState("");
  const [bottomUploading, setBottomUploading] = useState(false);
  const [bottomProgress, setBottomProgress] = useState(0);
  const [bottomSaved, setBottomSaved] = useState(false);
  const [bottomError, setBottomError] = useState("");
  const [bottomStagedUrl, setBottomStagedUrl] = useState(null);
  const bottomFileRef = useRef(null);

  const MAX_FILE_SIZE_MB = 500; // 500MB limit

  // Validate file
  const validateFile = (file) => {
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    const validExtensions = ['.mp4', '.webm', '.mov', '.avi', '.m4v'];
    
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      return { valid: false, error: "Please upload MP4 or WebM video files only." };
    }
    
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      return { valid: false, error: `File too large (${sizeMB.toFixed(0)}MB). Maximum size is ${MAX_FILE_SIZE_MB}MB.` };
    }
    
    return { valid: true, sizeMB };
  };

  // Handle hero file selection
  const handleHeroFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validation = validateFile(file);
    if (!validation.valid) {
      setHeroError(validation.error);
      return;
    }
    
    setHeroError("");
    setHeroFile(file);
    setHeroSaved(false);
    setHeroStagedUrl(null);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setHeroPreviewUrl(url);
  };

  // Upload file in chunks
  const uploadInChunks = async (file, caption, showcaseType, setProgress) => {
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    // Initialize upload
    const initRes = await axios.post(`${API}/admin/re-showcase/upload/init`, {
      filename: file.name,
      file_size: file.size,
      caption: caption,
      showcase_type: showcaseType
    });
    
    const uploadId = initRes.data.upload_id;
    
    // Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      const formData = new FormData();
      formData.append('upload_id', uploadId);
      formData.append('chunk_index', i);
      formData.append('chunk', chunk);
      
      await axios.post(`${API}/admin/re-showcase/upload/chunk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProgress(Math.round(((i + 1) / totalChunks) * 90)); // 0-90%
    }
    
    // Complete upload
    const completeRes = await axios.post(`${API}/admin/re-showcase/upload/complete`, {
      upload_id: uploadId
    });
    
    setProgress(100);
    return completeRes.data;
  };

  // Upload hero video (stages it, doesn't save to live yet)
  const handleHeroUpload = async () => {
    if (!heroFile) return;
    
    setHeroUploading(true);
    setHeroProgress(0);
    setHeroError("");
    
    try {
      const result = await uploadInChunks(heroFile, heroCaption, 're_showcase', setHeroProgress);
      setHeroStagedUrl(result.video_url);
      setReShowcase(result); // Update live immediately since backend saves on complete
      setHeroSaved(true);
      setHeroFile(null);
      if (heroFileRef.current) heroFileRef.current.value = "";
      
      // Clear preview after short delay
      setTimeout(() => {
        if (heroPreviewUrl) {
          URL.revokeObjectURL(heroPreviewUrl);
          setHeroPreviewUrl(null);
        }
      }, 1000);
    } catch (err) {
      console.error("Hero upload failed:", err);
      setHeroError(err.response?.data?.detail || err.message || "Upload failed");
    }
    
    setHeroUploading(false);
  };

  // Cancel hero upload
  const handleHeroCancel = () => {
    setHeroFile(null);
    if (heroPreviewUrl) {
      URL.revokeObjectURL(heroPreviewUrl);
      setHeroPreviewUrl(null);
    }
    setHeroCaption("");
    setHeroError("");
    setHeroSaved(false);
    if (heroFileRef.current) heroFileRef.current.value = "";
  };

  // Delete hero video
  const handleHeroDelete = async () => {
    if (!window.confirm("Are you sure you want to delete the hero video?")) return;
    
    try {
      await axios.delete(`${API}/admin/re-showcase`);
      setReShowcase({ video_url: null, caption: "" });
      setHeroSaved(false);
    } catch (err) {
      console.error("Delete failed:", err);
      setHeroError("Failed to delete video");
    }
  };

  // Handle bottom file selection
  const handleBottomFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validation = validateFile(file);
    if (!validation.valid) {
      setBottomError(validation.error);
      return;
    }
    
    setBottomError("");
    setBottomFile(file);
    setBottomSaved(false);
    setBottomStagedUrl(null);
    
    const url = URL.createObjectURL(file);
    setBottomPreviewUrl(url);
  };

  // Upload bottom video
  const handleBottomUpload = async () => {
    if (!bottomFile) return;
    
    setBottomUploading(true);
    setBottomProgress(0);
    setBottomError("");
    
    try {
      const result = await uploadInChunks(bottomFile, bottomCaption, 're_showcase2', setBottomProgress);
      setBottomStagedUrl(result.video_url);
      setReShowcase2(result);
      setBottomSaved(true);
      setBottomFile(null);
      if (bottomFileRef.current) bottomFileRef.current.value = "";
      
      setTimeout(() => {
        if (bottomPreviewUrl) {
          URL.revokeObjectURL(bottomPreviewUrl);
          setBottomPreviewUrl(null);
        }
      }, 1000);
    } catch (err) {
      console.error("Bottom upload failed:", err);
      setBottomError(err.response?.data?.detail || err.message || "Upload failed");
    }
    
    setBottomUploading(false);
  };

  // Cancel bottom upload
  const handleBottomCancel = () => {
    setBottomFile(null);
    if (bottomPreviewUrl) {
      URL.revokeObjectURL(bottomPreviewUrl);
      setBottomPreviewUrl(null);
    }
    setBottomCaption("");
    setBottomError("");
    setBottomSaved(false);
    if (bottomFileRef.current) bottomFileRef.current.value = "";
  };

  // Delete bottom video
  const handleBottomDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this video?")) return;
    
    try {
      await axios.delete(`${API}/admin/re-showcase2`);
      setReShowcase2({ video_url: null, caption: "" });
      setBottomSaved(false);
    } catch (err) {
      console.error("Delete failed:", err);
      setBottomError("Failed to delete video");
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero Video Section */}
      <div className="card-dark p-8 bg-zinc-900/80 backdrop-blur-sm border border-emerald-500/20">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Sparkles className="text-emerald-500" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-bold">Hero Video</h2>
            <p className="text-zinc-500 text-sm">Featured video at the top of the Real Estate landing page</p>
          </div>
        </div>

        {/* Current Video */}
        {reShowcase?.video_url && !heroPreviewUrl && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-zinc-400">Current Live Video</p>
              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">LIVE</span>
            </div>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/30 via-emerald-400/20 to-emerald-500/30 rounded-2xl blur-xl"></div>
              <div className="relative bg-black rounded-xl overflow-hidden ring-1 ring-emerald-500/30">
                <video
                  src={`${BACKEND_URL}${reShowcase.video_url}`}
                  controls
                  className="w-full aspect-video"
                  data-testid="re-hero-preview"
                />
              </div>
              {reShowcase.caption && (
                <p className="text-center text-zinc-400 mt-3 text-sm italic">"{reShowcase.caption}"</p>
              )}
              <p className="text-center text-xs text-zinc-600 mt-2">
                URL: {reShowcase.video_url}
              </p>
            </div>
            <div className="flex justify-center mt-4">
              <button
                onClick={handleHeroDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition"
              >
                <Trash2 size={16} /> Remove Video
              </button>
            </div>
          </div>
        )}

        {/* No Video Placeholder */}
        {!reShowcase?.video_url && !heroPreviewUrl && (
          <div className="mb-6">
            <div className="relative max-w-2xl mx-auto">
              <div className="bg-zinc-800/50 rounded-xl aspect-video flex items-center justify-center border-2 border-dashed border-zinc-700">
                <div className="text-center">
                  <Video className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500">No hero video uploaded</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview of Selected File */}
        {heroPreviewUrl && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-yellow-400 font-medium">Preview (Not Saved Yet)</p>
              <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">PREVIEW</span>
            </div>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/30 via-yellow-400/20 to-yellow-500/30 rounded-2xl blur-xl"></div>
              <div className="relative bg-black rounded-xl overflow-hidden ring-1 ring-yellow-500/30">
                <video
                  src={heroPreviewUrl}
                  controls
                  className="w-full aspect-video"
                />
              </div>
              <p className="text-center text-xs text-zinc-500 mt-2">
                {heroFile?.name} ({Math.round(heroFile?.size / (1024 * 1024))}MB)
              </p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {heroSaved && (
          <div className="mb-6 bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4 flex items-center gap-3">
            <Check className="text-emerald-400" size={20} />
            <div>
              <p className="text-emerald-400 font-medium">Video Saved Successfully!</p>
              <p className="text-emerald-400/70 text-sm">The hero video is now live on the Real Estate page.</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {heroError && (
          <div className="mb-6 bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="text-red-400" size={20} />
            <p className="text-red-400">{heroError}</p>
          </div>
        )}

        {/* Upload Form */}
        <div className="border-t border-zinc-700/50 pt-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Upload size={18} className="text-emerald-500" />
            {reShowcase?.video_url ? "Replace Hero Video" : "Upload Hero Video"}
          </h3>
          
          <div className="space-y-4">
            {/* Caption */}
            <div>
              <label className="block text-sm font-medium mb-2">Caption (Optional)</label>
              <input
                type="text"
                value={heroCaption}
                onChange={(e) => setHeroCaption(e.target.value)}
                placeholder="Stunning property tours that sell"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500"
                disabled={heroUploading}
              />
            </div>
            
            {/* File Input */}
            <div>
              <label className="block text-sm font-medium mb-2">Video File (MP4 or WebM, max {MAX_FILE_SIZE_MB}MB)</label>
              <input
                ref={heroFileRef}
                type="file"
                accept="video/mp4,video/webm,.mp4,.webm,.mov"
                onChange={handleHeroFileSelect}
                className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-500/20 file:text-emerald-400 file:font-semibold file:cursor-pointer hover:file:bg-emerald-500/30"
                disabled={heroUploading}
              />
            </div>
            
            {/* Progress */}
            {heroUploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Uploading...</span>
                  <span className="text-emerald-400">{heroProgress}%</span>
                </div>
                <div className="w-full bg-zinc-700 rounded-full h-2">
                  <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${heroProgress}%` }} />
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            {heroFile && !heroUploading && (
              <div className="flex gap-3">
                <button
                  onClick={handleHeroUpload}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black px-6 py-2.5 rounded-lg font-semibold flex items-center gap-2"
                >
                  <Upload size={16} /> Upload & Save
                </button>
                <button
                  onClick={handleHeroCancel}
                  className="bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-2.5 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
          <p className="text-emerald-400 text-sm">
            <strong>Tip:</strong> This video auto-plays (muted) on the Real Estate landing page. Use a high-quality property tour for best results.
          </p>
        </div>
      </div>

      {/* Bottom Video Section */}
      <div className="card-dark p-8 bg-zinc-900/80 backdrop-blur-sm border border-emerald-500/20">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Film className="text-emerald-500" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-bold">Bottom Example Video</h2>
            <p className="text-zinc-500 text-sm">"See What We Create" section video</p>
          </div>
        </div>

        {/* Current Video */}
        {reShowcase2?.video_url && !bottomPreviewUrl && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-zinc-400">Current Live Video</p>
              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">LIVE</span>
            </div>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/30 via-emerald-400/20 to-emerald-500/30 rounded-2xl blur-xl"></div>
              <div className="relative bg-black rounded-xl overflow-hidden ring-1 ring-emerald-500/30">
                <video
                  src={`${BACKEND_URL}${reShowcase2.video_url}`}
                  controls
                  className="w-full aspect-video"
                />
              </div>
              <p className="text-center text-xs text-zinc-600 mt-2">URL: {reShowcase2.video_url}</p>
            </div>
            <div className="flex justify-center mt-4">
              <button
                onClick={handleBottomDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition"
              >
                <Trash2 size={16} /> Remove Video
              </button>
            </div>
          </div>
        )}

        {/* No Video */}
        {!reShowcase2?.video_url && !bottomPreviewUrl && (
          <div className="mb-6">
            <div className="relative max-w-2xl mx-auto">
              <div className="bg-zinc-800/50 rounded-xl aspect-video flex items-center justify-center border-2 border-dashed border-zinc-700">
                <div className="text-center">
                  <Film className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500">No bottom video uploaded</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview */}
        {bottomPreviewUrl && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-yellow-400 font-medium">Preview (Not Saved Yet)</p>
              <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">PREVIEW</span>
            </div>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/30 via-yellow-400/20 to-yellow-500/30 rounded-2xl blur-xl"></div>
              <div className="relative bg-black rounded-xl overflow-hidden ring-1 ring-yellow-500/30">
                <video src={bottomPreviewUrl} controls className="w-full aspect-video" />
              </div>
              <p className="text-center text-xs text-zinc-500 mt-2">
                {bottomFile?.name} ({Math.round(bottomFile?.size / (1024 * 1024))}MB)
              </p>
            </div>
          </div>
        )}

        {/* Success */}
        {bottomSaved && (
          <div className="mb-6 bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4 flex items-center gap-3">
            <Check className="text-emerald-400" size={20} />
            <p className="text-emerald-400 font-medium">Video Saved Successfully!</p>
          </div>
        )}

        {/* Error */}
        {bottomError && (
          <div className="mb-6 bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="text-red-400" size={20} />
            <p className="text-red-400">{bottomError}</p>
          </div>
        )}

        {/* Upload Form */}
        <div className="border-t border-zinc-700/50 pt-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Upload size={18} className="text-emerald-500" />
            {reShowcase2?.video_url ? "Replace Video" : "Upload Video"}
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Caption (Optional)</label>
              <input
                type="text"
                value={bottomCaption}
                onChange={(e) => setBottomCaption(e.target.value)}
                placeholder="Watch the transformation"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500"
                disabled={bottomUploading}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Video File (MP4 or WebM, max {MAX_FILE_SIZE_MB}MB)</label>
              <input
                ref={bottomFileRef}
                type="file"
                accept="video/mp4,video/webm,.mp4,.webm,.mov"
                onChange={handleBottomFileSelect}
                className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-500/20 file:text-emerald-400 file:font-semibold file:cursor-pointer hover:file:bg-emerald-500/30"
                disabled={bottomUploading}
              />
            </div>
            
            {bottomUploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Uploading...</span>
                  <span className="text-emerald-400">{bottomProgress}%</span>
                </div>
                <div className="w-full bg-zinc-700 rounded-full h-2">
                  <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${bottomProgress}%` }} />
                </div>
              </div>
            )}
            
            {bottomFile && !bottomUploading && (
              <div className="flex gap-3">
                <button
                  onClick={handleBottomUpload}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black px-6 py-2.5 rounded-lg font-semibold flex items-center gap-2"
                >
                  <Upload size={16} /> Upload & Save
                </button>
                <button
                  onClick={handleBottomCancel}
                  className="bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-2.5 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Memorial Settings Panel — standalone admin component for 3 toggles
 */
const MemorialSettingsPanel = ({ token }) => {
  const [settings, setMSettings] = useState({
    enableCarousel: true,
    enableTopVideo: true,
    enableBottomVideo: true,
  });
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/memorial-settings/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMSettings(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = async (field) => {
    setToggling(true);
    try {
      const res = await axios.put(
        `${API}/memorial-settings/admin`,
        { [field]: !settings[field] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMSettings(res.data);
    } catch (err) {
      console.error('[MEMORIAL-SETTINGS] Toggle failed:', err);
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-yellow-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-400">{error}</p>
        <button onClick={fetchSettings} className="mt-4 px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg text-sm">
          Retry
        </button>
      </div>
    );
  }

  const toggles = [
    {
      field: 'enableCarousel',
      label: 'Enable Memorial Carousel',
      description: 'Show the Click-to-Animate photo carousel directly under the hero section on the Memorial page',
    },
    {
      field: 'enableTopVideo',
      label: 'Enable Memorial Top Video',
      description: 'Show the top hero video section on the Memorial page (below carousel)',
    },
    {
      field: 'enableBottomVideo',
      label: 'Enable Memorial Bottom Video',
      description: 'Show the bottom demo video section on the Memorial page',
    },
  ];

  return (
    <div className="space-y-6" data-testid="memorial-settings-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Settings size={20} className="text-yellow-400" />
            Memorial Settings
          </h2>
          <p className="text-zinc-400 text-sm mt-1">Control which sections appear on the public Memorial page</p>
        </div>
        <button onClick={fetchSettings} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors" title="Refresh">
          <RefreshCw className="w-5 h-5 text-zinc-400" />
        </button>
      </div>

      <div className="space-y-3">
        {toggles.map((t) => (
          <div key={t.field} className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700" data-testid={`toggle-${t.field}`}>
            <div>
              <p className="text-white font-medium">{t.label}</p>
              <p className="text-zinc-500 text-xs mt-0.5">{t.description}</p>
            </div>
            <button
              onClick={() => handleToggle(t.field)}
              disabled={toggling}
              className={`relative transition-all duration-200 ${toggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid={`toggle-btn-${t.field}`}
            >
              {settings[t.field] ? (
                <ToggleRight className="w-10 h-10 text-green-400" />
              ) : (
                <ToggleLeft className="w-10 h-10 text-zinc-500" />
              )}
            </button>
          </div>
        ))}
      </div>

      <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/20">
        <p className="text-yellow-400 text-sm font-medium mb-1">How it works</p>
        <ul className="text-zinc-400 text-xs space-y-1">
          <li>Changes take effect immediately on the public Memorial page.</li>
          <li>Turning a section OFF removes it completely — no placeholder or spacing.</li>
          <li>The carousel appears directly under the "Create Memorial Video" button when enabled.</li>
        </ul>
      </div>
    </div>
  );
};

// This component receives useAuth as a prop from App.js
const AdminPage = ({ useAuth }) => {
  const { user, token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("customers");
  const [activeSubTab, setActiveSubTab] = useState("settings"); // Sub-tab for Memorial/Real Estate
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [examples, setExamples] = useState([]);
  const [reExamples, setReExamples] = useState([]);
  const [reShowcase, setReShowcase] = useState({ video_url: null, caption: "" });
  const [reShowcaseFile, setReShowcaseFile] = useState(null);
  const [reShowcaseCaption, setReShowcaseCaption] = useState("");
  const [reShowcaseUploading, setReShowcaseUploading] = useState(false);
  const [reShowcaseProgress, setReShowcaseProgress] = useState(0);
  const [reShowcase2, setReShowcase2] = useState({ video_url: null, caption: "" });
  const [reShowcase2File, setReShowcase2File] = useState(null);
  const [reShowcase2Caption, setReShowcase2Caption] = useState("");
  const [reShowcase2Uploading, setReShowcase2Uploading] = useState(false);
  const [reShowcase2Progress, setReShowcase2Progress] = useState(0);
  // Memorial Showcase state
  const [memorialShowcase, setMemorialShowcase] = useState({ video_url: null, caption: "" });
  const [memorialShowcaseFile, setMemorialShowcaseFile] = useState(null);
  const [memorialShowcaseCaption, setMemorialShowcaseCaption] = useState("");
  const [memorialShowcaseUploading, setMemorialShowcaseUploading] = useState(false);
  const [memorialShowcaseProgress, setMemorialShowcaseProgress] = useState(0);
  const [memorialShowcase2, setMemorialShowcase2] = useState({ video_url: null, caption: "" });
  const [memorialShowcase2File, setMemorialShowcase2File] = useState(null);
  const [memorialShowcase2Caption, setMemorialShowcase2Caption] = useState("");
  const [memorialShowcase2Uploading, setMemorialShowcase2Uploading] = useState(false);
  const [memorialShowcase2Progress, setMemorialShowcase2Progress] = useState(0);
  const [settings, setSettings] = useState({ editor_email: "", support_email: "", orders_paused: false, pause_message: "", re_test_mode: false });
  const [analytics, setAnalytics] = useState(null);
  const [storageStats, setStorageStats] = useState(null);
  const [showcase, setShowcase] = useState([
    { slot: 1, enabled: false, photo_url: null, video_url: null, caption: "Click to bring this memory to life" },
    { slot: 2, enabled: false, photo_url: null, video_url: null, caption: "Click to bring this memory to life" },
    { slot: 3, enabled: false, photo_url: null, video_url: null, caption: "Click to bring this memory to life" }
  ]);
  const [showcaseUploading, setShowcaseUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [exampleTitle, setExampleTitle] = useState("");
  const [exampleDesc, setExampleDesc] = useState("");
  const [reExampleTitle, setReExampleTitle] = useState("");
  const [reExampleDesc, setReExampleDesc] = useState("");
  const [reExampleFile, setReExampleFile] = useState(null);
  const [reUploading, setReUploading] = useState(false);
  const [videoBookFile, setVideoBookFile] = useState(null);
  const [videoBookUploading, setVideoBookUploading] = useState(false);
  const [videoBookData, setVideoBookData] = useState(null);
  const exampleFileInputRef = useRef(null);
  const reExampleFileInputRef = useRef(null);
  const reShowcaseFileRef = useRef(null);
  const reShowcase2FileRef = useRef(null);
  const memorialShowcaseFileRef = useRef(null);
  const memorialShowcase2FileRef = useRef(null);
  const showcasePhotoRef = useRef(null);
  const showcaseVideoRef = useRef(null);
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [verticalFilter, setVerticalFilter] = useState("all"); // "all", "memorial", "real_estate"

  // Helper to trigger data refresh
  const refreshData = () => setRefreshKey(k => k + 1);

  // Fetch storage stats periodically
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    
    const fetchStorage = async () => {
      try {
        const res = await axios.get(`${API}/admin/storage`);
        setStorageStats(res.data);
      } catch (err) {
        console.error("Failed to fetch storage stats:", err);
      }
    };
    
    fetchStorage();
    const interval = setInterval(fetchStorage, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    
    // Fetch admin data based on active tab
    (async () => {
      setLoading(true);
      try {
        if (activeTab === "customers") {
          const res = await axios.get(`${API}/admin/customers`);
          setCustomers(res.data);
        } else if (activeTab === "orders") {
          const res = await axios.get(`${API}/orders`);
          setOrders(res.data);
        } else if (activeTab === "settings") {
          const res = await axios.get(`${API}/admin/settings`);
          setSettings(res.data);
        } else if (activeTab === "memorial") {
          // Load Memorial-specific data
          if (activeSubTab === "examples") {
            const res = await axios.get(`${API}/examples?vertical=memorial`);
            setExamples(res.data);
          } else if (activeSubTab === "showcase") {
            const res = await axios.get(`${API}/admin/showcase`);
            setShowcase(res.data);
            // Also fetch memorial showcase videos
            const showcaseRes = await axios.get(`${API}/admin/memorial-showcase`);
            setMemorialShowcase(showcaseRes.data);
            setMemorialShowcaseCaption(showcaseRes.data.caption || "");
            const showcase2Res = await axios.get(`${API}/admin/memorial-showcase2`);
            setMemorialShowcase2(showcase2Res.data);
            setMemorialShowcase2Caption(showcase2Res.data.caption || "");
          } else if (activeSubTab === "videobook") {
            const res = await axios.get(`${API}/admin/videobook`);
            setVideoBookData(res.data);
          } else if (activeSubTab === "analytics") {
            const res = await axios.get(`${API}/admin/analytics`);
            setAnalytics(res.data);
          }
        } else if (activeTab === "real_estate") {
          // Load Real Estate-specific data
          if (activeSubTab === "examples") {
            const res = await axios.get(`${API}/examples?vertical=real_estate`);
            setReExamples(res.data);
          } else if (activeSubTab === "showcase") {
            const res = await axios.get(`${API}/admin/re-showcase`);
            setReShowcase(res.data);
            setReShowcaseCaption(res.data.caption || "");
            // Also fetch showcase 2
            const res2 = await axios.get(`${API}/admin/re-showcase2`);
            setReShowcase2(res2.data);
            setReShowcase2Caption(res2.data.caption || "");
          } else if (activeSubTab === "analytics") {
            const res = await axios.get(`${API}/admin/analytics`);
            setAnalytics(res.data);
            // Also load orders for RE analytics
            const ordersRes = await axios.get(`${API}/orders`);
            setOrders(ordersRes.data);
          }
        }
      } catch (err) {
        console.error("Failed to fetch admin data:", err);
      }
      setLoading(false);
    })();
  }, [user, authLoading, activeTab, activeSubTab, navigate, refreshKey]);

  // Real Estate Showcase handlers
  // Chunked upload helper function
  const uploadFileInChunks = async (file, caption, showcaseType, setProgress) => {
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks - safe for proxy limits
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    console.log(`[Chunked Upload] Starting ${showcaseType}: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB) in ${totalChunks} chunks`);
    
    // Step 1: Initialize upload session
    const initRes = await axios.post(`${API}/admin/re-showcase/upload/init`, {
      filename: file.name,
      file_size: file.size,
      caption: caption,
      showcase_type: showcaseType
    });
    
    const uploadId = initRes.data.upload_id;
    console.log(`[Chunked Upload] Session initialized: ${uploadId}`);
    
    // Step 2: Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      const formData = new FormData();
      formData.append('upload_id', uploadId);
      formData.append('chunk_index', i.toString());
      formData.append('chunk', chunk, `chunk_${i}`);
      
      await axios.post(`${API}/admin/re-showcase/upload/chunk`, formData, {
        timeout: 60000, // 1 min per chunk
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const progress = Math.round(((i + 1) / totalChunks) * 95); // Reserve 5% for finalization
      setProgress(progress);
      console.log(`[Chunked Upload] Chunk ${i + 1}/${totalChunks} uploaded (${progress}%)`);
    }
    
    // Step 3: Complete upload
    setProgress(98);
    const completeRes = await axios.post(`${API}/admin/re-showcase/upload/complete`, {
      upload_id: uploadId
    });
    
    setProgress(100);
    console.log(`[Chunked Upload] Complete:`, completeRes.data);
    return completeRes.data;
  };

  const handleUploadReShowcase = async () => {
    if (!reShowcaseFile) {
      alert("Please select a video file");
      return;
    }

    const fileSizeMB = (reShowcaseFile.size / (1024 * 1024)).toFixed(2);
    console.log("[RE Showcase Upload] Starting upload...");
    console.log("[RE Showcase Upload] File details:", {
      name: reShowcaseFile.name,
      size: `${fileSizeMB} MB`,
      type: reShowcaseFile.type
    });

    setReShowcaseUploading(true);
    setReShowcaseProgress(0);
    
    try {
      const result = await uploadFileInChunks(
        reShowcaseFile, 
        reShowcaseCaption, 
        're_showcase',
        setReShowcaseProgress
      );

      console.log("[RE Showcase Upload] Success:", result);
      setReShowcase(result);
      setReShowcaseFile(null);
      setReShowcaseProgress(0);
      if (reShowcaseFileRef.current) reShowcaseFileRef.current.value = "";
      alert("Showcase video uploaded successfully!");
    } catch (err) {
      console.error("[RE Showcase Upload] FAILED:", err);
      setReShowcaseProgress(0);
      
      let errorMsg = "Upload failed. ";
      if (err.response) {
        errorMsg += err.response.data?.detail || `Error ${err.response.status}`;
      } else {
        errorMsg += err.message || "Network error";
      }
      alert(errorMsg);
    }
    setReShowcaseUploading(false);
  };

  const handleDeleteReShowcase = async () => {
    if (!window.confirm("Are you sure you want to delete the showcase video?")) return;

    try {
      await axios.delete(`${API}/admin/re-showcase`);
      setReShowcase({ video_url: null, caption: "" });
      setReShowcaseCaption("");
      alert("Showcase video deleted.");
    } catch (err) {
      console.error("Failed to delete RE showcase:", err);
      alert("Failed to delete video.");
    }
  };

  // Real Estate Showcase 2 (Demo Video) handlers
  const handleUploadReShowcase2 = async () => {
    if (!reShowcase2File) {
      alert("Please select a video file");
      return;
    }

    const fileSizeMB = (reShowcase2File.size / (1024 * 1024)).toFixed(2);
    console.log("[RE Showcase 2 Upload] Starting upload:", fileSizeMB, "MB");

    setReShowcase2Uploading(true);
    setReShowcase2Progress(0);
    
    try {
      const result = await uploadFileInChunks(
        reShowcase2File,
        reShowcase2Caption,
        're_showcase2',
        setReShowcase2Progress
      );

      setReShowcase2(result);
      setReShowcase2File(null);
      setReShowcase2Progress(0);
      if (reShowcase2FileRef.current) reShowcase2FileRef.current.value = "";
      alert("Demo video uploaded successfully!");
    } catch (err) {
      console.error("Failed to upload RE showcase 2:", err);
      setReShowcase2Progress(0);
      alert(`Upload failed: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
    }
    setReShowcase2Uploading(false);
  };

  const handleDeleteReShowcase2 = async () => {
    if (!window.confirm("Are you sure you want to delete the demo video?")) return;

    try {
      await axios.delete(`${API}/admin/re-showcase2`);
      setReShowcase2({ video_url: null, caption: "" });
      setReShowcase2Caption("");
      alert("Demo video deleted.");
    } catch (err) {
      console.error("Failed to delete RE showcase 2:", err);
      alert("Failed to delete video.");
    }
  };

  // Memorial Showcase handlers
  const handleUploadMemorialShowcase = async () => {
    if (!memorialShowcaseFile) {
      alert("Please select a video file");
      return;
    }

    const fileSizeMB = (memorialShowcaseFile.size / (1024 * 1024)).toFixed(2);
    console.log("[Memorial Showcase Upload] Starting upload:", fileSizeMB, "MB");

    setMemorialShowcaseUploading(true);
    setMemorialShowcaseProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('video', memorialShowcaseFile);
      formData.append('caption', memorialShowcaseCaption);
      
      const res = await axios.post(`${API}/admin/memorial-showcase/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setMemorialShowcaseProgress(progress);
        }
      });

      setMemorialShowcase(res.data);
      setMemorialShowcaseFile(null);
      setMemorialShowcaseProgress(0);
      if (memorialShowcaseFileRef.current) memorialShowcaseFileRef.current.value = "";
      alert("Memorial showcase video uploaded successfully!");
    } catch (err) {
      console.error("Failed to upload memorial showcase:", err);
      setMemorialShowcaseProgress(0);
      alert(`Upload failed: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
    }
    setMemorialShowcaseUploading(false);
  };

  const handleDeleteMemorialShowcase = async () => {
    if (!window.confirm("Are you sure you want to delete the memorial showcase video?")) return;

    try {
      await axios.delete(`${API}/admin/memorial-showcase`);
      setMemorialShowcase({ video_url: null, caption: "" });
      setMemorialShowcaseCaption("");
      alert("Memorial showcase video deleted.");
    } catch (err) {
      console.error("Failed to delete memorial showcase:", err);
      alert("Failed to delete video.");
    }
  };

  // Memorial Showcase 2 handlers
  const handleUploadMemorialShowcase2 = async () => {
    if (!memorialShowcase2File) {
      alert("Please select a video file");
      return;
    }

    const fileSizeMB = (memorialShowcase2File.size / (1024 * 1024)).toFixed(2);
    console.log("[Memorial Showcase 2 Upload] Starting upload:", fileSizeMB, "MB");

    setMemorialShowcase2Uploading(true);
    setMemorialShowcase2Progress(0);
    
    try {
      const formData = new FormData();
      formData.append('video', memorialShowcase2File);
      formData.append('caption', memorialShowcase2Caption);
      
      const res = await axios.post(`${API}/admin/memorial-showcase2/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setMemorialShowcase2Progress(progress);
        }
      });

      setMemorialShowcase2(res.data);
      setMemorialShowcase2File(null);
      setMemorialShowcase2Progress(0);
      if (memorialShowcase2FileRef.current) memorialShowcase2FileRef.current.value = "";
      alert("Memorial bottom video uploaded successfully!");
    } catch (err) {
      console.error("Failed to upload memorial showcase 2:", err);
      setMemorialShowcase2Progress(0);
      alert(`Upload failed: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
    }
    setMemorialShowcase2Uploading(false);
  };

  const handleDeleteMemorialShowcase2 = async () => {
    if (!window.confirm("Are you sure you want to delete the memorial bottom video?")) return;

    try {
      await axios.delete(`${API}/admin/memorial-showcase2`);
      setMemorialShowcase2({ video_url: null, caption: "" });
      setMemorialShowcase2Caption("");
      alert("Memorial bottom video deleted.");
    } catch (err) {
      console.error("Failed to delete memorial showcase 2:", err);
      alert("Failed to delete video.");
    }
  };

  // Video Book handlers
  const handleUploadVideoBook = async () => {
    if (!videoBookFile) return;
    
    setVideoBookUploading(true);
    try {
      const formData = new FormData();
      formData.append('video', videoBookFile);
      
      const res = await axios.post(`${API}/admin/videobook/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setVideoBookData(res.data);
      setVideoBookFile(null);
      alert("Video Book video uploaded successfully!");
    } catch (err) {
      console.error("Failed to upload video book video:", err);
      alert("Failed to upload video. Please try again.");
    }
    setVideoBookUploading(false);
  };

  const handleDeleteVideoBook = async () => {
    if (!window.confirm("Are you sure you want to delete the Video Book video?")) return;
    
    try {
      await axios.delete(`${API}/admin/videobook`);
      setVideoBookData(null);
      alert("Video Book video deleted.");
    } catch (err) {
      console.error("Failed to delete video book video:", err);
      alert("Failed to delete video.");
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, settings);
      alert("Settings saved!");
    } catch (err) {
      console.error("Failed to save settings:", err);
      alert("Failed to save settings");
    }
  };

  const [exampleFile, setExampleFile] = useState(null);

  const handleExampleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setExampleFile(file);
    }
  };

  const handleUploadExample = async () => {
    if (!exampleFile || !exampleTitle.trim()) {
      alert("Please enter a title and select a video file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", exampleFile);
    formData.append("title", exampleTitle);
    formData.append("description", exampleDesc);
    formData.append("vertical", "memorial");

    try {
      await axios.post(`${API}/admin/examples/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setExampleTitle("");
      setExampleDesc("");
      setExampleFile(null);
      if (exampleFileInputRef.current) exampleFileInputRef.current.value = "";
      refreshData();
    } catch (err) {
      console.error("Failed to upload example:", err);
      alert("Failed to upload example video");
    }
    setUploading(false);
  };

  // Real Estate Example Video Handlers
  const handleReExampleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setReExampleFile(file);
    }
  };

  const handleUploadReExample = async () => {
    if (!reExampleTitle.trim()) {
      alert("Please enter a title for the example video");
      return;
    }
    if (!reExampleFile) {
      alert("Please select a video file");
      return;
    }

    setReUploading(true);
    const formData = new FormData();
    formData.append("file", reExampleFile);
    formData.append("title", reExampleTitle);
    formData.append("description", reExampleDesc);
    formData.append("vertical", "real_estate");

    try {
      await axios.post(`${API}/admin/examples/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setReExampleTitle("");
      setReExampleDesc("");
      setReExampleFile(null);
      if (reExampleFileInputRef.current) reExampleFileInputRef.current.value = "";
      refreshData();
    } catch (err) {
      console.error("Failed to upload RE example:", err);
      alert("Failed to upload example video");
    }
    setReUploading(false);
  };

  const handleDeleteReExample = async (exampleId) => {
    if (!window.confirm("Delete this example video?")) return;
    try {
      await axios.delete(`${API}/admin/examples/${exampleId}`);
      refreshData();
    } catch (err) {
      console.error("Failed to delete RE example:", err);
    }
  };

  const handleDeleteExample = async (exampleId) => {
    if (!window.confirm("Delete this example video?")) return;
    try {
      await axios.delete(`${API}/admin/examples/${exampleId}`);
      refreshData();
    } catch (err) {
      console.error("Failed to delete example:", err);
    }
  };

  const handleReorderExamples = async (dragIndex, dropIndex) => {
    const newExamples = [...examples];
    const [removed] = newExamples.splice(dragIndex, 1);
    newExamples.splice(dropIndex, 0, removed);
    setExamples(newExamples);

    try {
      await axios.put(`${API}/admin/examples/reorder`, {
        example_ids: newExamples.map(e => e.id)
      });
    } catch (e) {
      console.error("Failed to reorder examples:", e);
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/export/${type}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      alert("Export failed");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <BackButton to="/dashboard" label="Back to Dashboard" />
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold font-serif">Admin Dashboard</h1>
          {/* Export Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('customers')}
              className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
              data-testid="export-customers"
            >
              <FileSpreadsheet size={16} /> Customers CSV
            </button>
            <button
              onClick={() => handleExport('orders')}
              className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
              data-testid="export-orders"
            >
              <FileSpreadsheet size={16} /> Orders CSV
            </button>
            <button
              onClick={() => handleExport('analytics')}
              className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
              data-testid="export-analytics"
            >
              <BarChart3 size={16} /> Analytics CSV
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { id: "customers", label: "Customers", icon: <Users size={18} /> },
            { id: "orders", label: "Orders", icon: <Package size={18} /> },
            { id: "memorial", label: "Memorial", icon: <Heart size={18} />, color: "yellow" },
            { id: "real_estate", label: "Real Estate", icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>, color: "emerald" },
            { id: "settings", label: "Settings", icon: <Settings size={18} /> },
            { id: "danger", label: "Danger Zone", icon: <AlertCircle size={18} />, color: "red" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === "memorial") {
                  setActiveSubTab("settings");
                } else if (tab.id === "real_estate") {
                  setActiveSubTab("videos");
                }
              }}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition ${
                activeTab === tab.id 
                  ? tab.color === "emerald" 
                    ? 'bg-emerald-500 text-black' 
                    : tab.color === "yellow"
                      ? 'bg-yellow-500 text-black'
                      : tab.color === "red"
                        ? 'bg-red-500 text-white'
                        : 'bg-yellow-500 text-black'
                  : tab.color === "red"
                    ? 'bg-zinc-800 hover:bg-red-900/50 text-red-400'
                    : 'bg-zinc-800 hover:bg-zinc-700'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Sub-tabs for Memorial */}
        {activeTab === "memorial" && (
          <div className="flex gap-2 mb-6 border-l-4 border-yellow-500 pl-4 flex-wrap">
            {[
              { id: "settings", label: "Settings", icon: <Settings size={16} /> },
              { id: "moments", label: "Moments", icon: <Sparkles size={16} /> },
              { id: "videos", label: "Page Videos", icon: <Video size={16} /> },
              { id: "videobook", label: "Video Book", icon: <BookOpen size={16} /> },
              { id: "analytics", label: "Analytics", icon: <BarChart3 size={16} /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm transition ${
                  activeSubTab === tab.id ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Sub-tabs for Real Estate */}
        {activeTab === "real_estate" && (
          <div className="flex gap-2 mb-6 border-l-4 border-emerald-500 pl-4">
            {[
              { id: "videos", label: "Page Videos", icon: <Video size={16} /> },
              { id: "analytics", label: "Analytics", icon: <BarChart3 size={16} /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm transition ${
                  activeSubTab === tab.id ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* RE Video Manager - Only show on Real Estate -> Videos tab */}
        {activeTab === "real_estate" && activeSubTab === "videos" && (
          <REVideoManager />
        )}

        {/* Memorial Video Manager - Only show on Memorial -> Videos tab */}
        {activeTab === "memorial" && activeSubTab === "videos" && (
          <MemorialVideoManager />
        )}

        {/* Memorial Moments Manager - Only show on Memorial -> Moments tab */}
        {activeTab === "memorial" && activeSubTab === "moments" && (
          <MemorialMomentsManager token={token} />
        )}

        {/* Memorial Settings Panel - Only show on Memorial -> Settings tab */}
        {activeTab === "memorial" && activeSubTab === "settings" && (
          <MemorialSettingsPanel token={token} />
        )}

        {/* System Health Panel - Only show on danger zone tab */}
        {activeTab === "danger" && (
          <SystemHealthPanel />
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6" data-testid="admin-settings-panel">
            <div className="card-dark p-6">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Settings size={20} className="text-yellow-400" />
                Notification Emails
              </h2>

              <div className="space-y-5">
                {/* Editor Email */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Editor Email</label>
                  <p className="text-xs text-zinc-500 mb-2">Receives order notifications and photo upload alerts for editing.</p>
                  <input
                    type="email"
                    value={settings.editor_email || ''}
                    onChange={(e) => setSettings({...settings, editor_email: e.target.value})}
                    placeholder="editor@example.com"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none"
                    data-testid="settings-editor-email"
                  />
                </div>

                {/* Support / Admin Email */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Admin Email (You)</label>
                  <p className="text-xs text-zinc-500 mb-2">Receives a copy of every order notification, payment confirmation, and upload alert.</p>
                  <input
                    type="email"
                    value={settings.support_email || ''}
                    onChange={(e) => setSettings({...settings, support_email: e.target.value})}
                    placeholder="you@example.com"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none"
                    data-testid="settings-support-email"
                  />
                </div>
              </div>
            </div>

            {/* Orders Paused */}
            <div className="card-dark p-6">
              <h2 className="text-xl font-bold mb-4">Order Controls</h2>
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.orders_paused || false}
                    onChange={(e) => setSettings({...settings, orders_paused: e.target.checked})}
                    className="w-5 h-5 accent-yellow-500"
                    data-testid="settings-orders-paused"
                  />
                  <div>
                    <span className="font-medium">Pause All Orders</span>
                    <p className="text-xs text-zinc-500">Temporarily stop accepting new orders.</p>
                  </div>
                </label>

                {settings.orders_paused && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-1">Pause Message</label>
                    <input
                      type="text"
                      value={settings.pause_message || ''}
                      onChange={(e) => setSettings({...settings, pause_message: e.target.value})}
                      placeholder="We are currently not accepting new orders."
                      className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none"
                      data-testid="settings-pause-message"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Test Mode */}
            <div className="card-dark p-6">
              <h2 className="text-xl font-bold mb-4">Test Mode</h2>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.re_test_mode || false}
                    onChange={(e) => setSettings({...settings, re_test_mode: e.target.checked})}
                    className="w-5 h-5 accent-emerald-500"
                    data-testid="settings-re-test-mode"
                  />
                  <div>
                    <span className="font-medium">Real Estate Test Mode</span>
                    <p className="text-xs text-zinc-500">Use Stripe test keys. No real charges.</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.memorial_test_mode || false}
                    onChange={(e) => setSettings({...settings, memorial_test_mode: e.target.checked})}
                    className="w-5 h-5 accent-yellow-500"
                    data-testid="settings-memorial-test-mode"
                  />
                  <div>
                    <span className="font-medium">Memorial Test Mode</span>
                    <p className="text-xs text-zinc-500">Use Stripe test keys. No real charges.</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveSettings}
              className="btn-primary px-8 py-3 text-lg"
              data-testid="settings-save-btn"
            >
              Save Settings
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Customers Tab */}
            {activeTab === "customers" && (
              <div className="card-dark overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-800">
                    <tr>
                      <th className="text-left px-6 py-3 text-sm font-medium">Name</th>
                      <th className="text-left px-6 py-3 text-sm font-medium">Email</th>
                      <th className="text-left px-6 py-3 text-sm font-medium">Marketing</th>
                      <th className="text-left px-6 py-3 text-sm font-medium">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.id} className="border-t border-zinc-800">
                        <td className="px-6 py-4">{c.full_name}</td>
                        <td className="px-6 py-4 text-zinc-400">{c.email}</td>
                        <td className="px-6 py-4">
                          {c.marketing_opt_in ? (
                            <span className="text-green-500">✓ Opted in</span>
                          ) : (
                            <span className="text-zinc-500">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-zinc-400">
                          {new Date(c.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {customers.length === 0 && (
                  <div className="text-center py-12 text-zinc-500">No customers yet</div>
                )}
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === "orders" && (
              <div className="space-y-4">
                {/* Vertical Filter */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm text-zinc-400">Filter:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setVerticalFilter("all")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        verticalFilter === "all" 
                          ? "bg-zinc-700 text-white" 
                          : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                      }`}
                    >
                      All ({orders.length})
                    </button>
                    <button
                      onClick={() => setVerticalFilter("memorial")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1.5 ${
                        verticalFilter === "memorial" 
                          ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/50" 
                          : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                      }`}
                    >
                      <Heart size={14} /> Memorial ({orders.filter(o => o.vertical !== "real_estate").length})
                    </button>
                    <button
                      onClick={() => setVerticalFilter("real_estate")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1.5 ${
                        verticalFilter === "real_estate" 
                          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/50" 
                          : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                      }`}
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                      Real Estate ({orders.filter(o => o.vertical === "real_estate").length})
                    </button>
                  </div>
                </div>

                {/* Orders List */}
                {orders
                  .filter(order => {
                    if (verticalFilter === "all") return true;
                    if (verticalFilter === "memorial") return order.vertical !== "real_estate";
                    return order.vertical === verticalFilter;
                  })
                  .map((order) => {
                  const timeline = order.timeline || {};
                  const currentMilestone = timeline.milestones?.find(m => m.is_current);
                  const isRush = timeline.is_rush;
                  
                  return (
                    <div
                      key={order.id}
                      className={`card-dark p-4 hover:border-zinc-700 cursor-pointer transition ${isRush ? 'border-red-500/50' : ''}`}
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-bold">{order.order_number}</span>
                            <VerticalBadge vertical={order.vertical} />
                            {isRush && <RushBadge />}
                            {order.video_book && <VideoBookBadge />}
                            <StatusBadge status={order.status} />
                          </div>
                          <div className="text-sm text-zinc-400 mt-1">
                            {order.customer_name} • {order.customer_email}
                          </div>
                          
                          {/* RUSH Timeline Progress for Admin/Editor */}
                          {isRush && timeline.start_date && order.status !== "completed" && (
                            <div className="mt-2">
                              <div className="flex items-center gap-3">
                                <div className="flex-1 max-w-xs bg-zinc-700 rounded-full h-1.5">
                                  <div 
                                    className={`h-1.5 rounded-full ${timeline.rush_deadline_passed ? 'bg-red-500' : 'bg-orange-500'}`}
                                    style={{ width: `${Math.min(100, (timeline.hours_elapsed / 48) * 100)}%` }}
                                  ></div>
                                </div>
                                <span className={`text-xs font-medium ${timeline.rush_deadline_passed ? 'text-red-400' : 'text-orange-400'}`}>
                                  {timeline.rush_hours_remaining > 0 
                                    ? `${Math.round(timeline.rush_hours_remaining)}h left`
                                    : 'OVERDUE'
                                  }
                                </span>
                              </div>
                              <div className="text-xs text-orange-400 mt-1">
                                Deadline: {timeline.rush_deadline_display}
                              </div>
                            </div>
                          )}
                          
                          {/* Standard Timeline Progress for Admin */}
                          {!isRush && timeline.milestones && timeline.start_date && order.status !== "completed" && (
                            <div className="mt-2 flex items-center gap-3">
                              <div className="flex-1 max-w-xs bg-zinc-700 rounded-full h-1.5">
                                <div 
                                  className="bg-yellow-500 h-1.5 rounded-full"
                                  style={{ width: `${Math.min(100, (timeline.days_elapsed / 5) * 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-yellow-500">
                                {currentMilestone?.label || 'Pending'} (Day {timeline.days_elapsed})
                              </span>
                            </div>
                          )}
                          
                          {order.status === "completed" && timeline.is_early_complete && (
                            <div className="text-xs text-green-500 mt-1 flex items-center gap-1">
                              <Zap size={12} /> Completed Early
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-yellow-500">${order.total_amount?.toLocaleString()}</div>
                          <div className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleDateString()}</div>
                          {!isRush && timeline.milestones && order.status !== "completed" && (
                            <div className="text-xs text-zinc-500 mt-1">
                              ETA: {timeline.milestones[timeline.milestones.length - 1]?.date}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {orders.filter(order => {
                  if (verticalFilter === "all") return true;
                  if (verticalFilter === "memorial") return order.vertical !== "real_estate";
                  return order.vertical === verticalFilter;
                }).length === 0 && (
                  <div className="card-dark p-12 text-center text-zinc-500">
                    {verticalFilter === "all" ? "No orders yet" : `No ${verticalFilter === "memorial" ? "memorial" : "real estate"} orders`}
                  </div>
                )}
              </div>
            )}

            {/* Showcase Tab */}
            {/* Memorial Tab Content */}
            {activeTab === "memorial" && activeSubTab === "showcase" && (
              <div className="max-w-4xl">
                <div className="card-dark p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Sparkles className="text-yellow-500" size={24} />
                    <h2 className="text-xl font-semibold">Photo-to-Video Showcase Carousel</h2>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">
                    Manage up to 3 showcase items that rotate in a carousel on the landing page. Each slot shows a still photo that transforms into a video.
                  </p>
                  
                  {/* Showcase Slots */}
                  <div className="space-y-8">
                    {Array.isArray(showcase) && showcase.map((slot, index) => (
                      <div key={slot.slot || index} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg">Showcase {slot.slot || index + 1}</h3>
                          <button
                            onClick={() => {
                              const updated = [...showcase];
                              updated[index] = { ...updated[index], enabled: !updated[index].enabled };
                              setShowcase(updated);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              slot.enabled ? 'bg-yellow-500' : 'bg-zinc-600'
                            }`}
                            data-testid={`showcase-${slot.slot || index + 1}-toggle`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              slot.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>

                        {/* Caption */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-2">Caption</label>
                          <input
                            type="text"
                            value={slot.caption || ""}
                            onChange={(e) => {
                              const updated = [...showcase];
                              updated[index] = { ...updated[index], caption: e.target.value };
                              setShowcase(updated);
                            }}
                            placeholder="Click to bring this memory to life"
                            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 focus:outline-none focus:border-yellow-500 text-sm"
                          />
                        </div>

                        {/* Preview */}
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-zinc-500 mb-2">Photo (Before)</p>
                            {slot.photo_url ? (
                              <div className="relative group">
                                <img 
                                  src={`${BACKEND_URL}${slot.photo_url}`} 
                                  alt={`Showcase ${slot.slot || index + 1}`}
                                  className="w-full aspect-video object-contain rounded-lg bg-zinc-900"
                                />
                                <button
                                  onClick={() => {
                                    const updated = [...showcase];
                                    updated[index] = { ...updated[index], photo_url: null, _clearPhoto: true };
                                    setShowcase(updated);
                                  }}
                                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove photo"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ) : (
                              <div className="w-full aspect-video bg-zinc-900 rounded-lg flex items-center justify-center text-zinc-600 text-sm">
                                No photo
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500 mb-2">Video (After)</p>
                            {slot.video_url ? (
                              <div className="relative group">
                                <video 
                                  src={`${BACKEND_URL}${slot.video_url}`} 
                                  controls 
                                  muted 
                                  className="w-full aspect-video object-contain rounded-lg bg-zinc-900"
                                />
                                <button
                                  onClick={() => {
                                    const updated = [...showcase];
                                    updated[index] = { ...updated[index], video_url: null, _clearVideo: true };
                                    setShowcase(updated);
                                  }}
                                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove video"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ) : (
                              <div className="w-full aspect-video bg-zinc-900 rounded-lg flex items-center justify-center text-zinc-600 text-sm">
                                No video
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Upload Inputs */}
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium mb-1 text-zinc-400">Upload Photo</label>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              id={`showcase-photo-${slot.slot || index + 1}`}
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  const updated = [...showcase];
                                  updated[index] = { ...updated[index], _newPhotoName: e.target.files[0].name };
                                  setShowcase(updated);
                                }
                              }}
                              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-yellow-500 file:text-black file:font-semibold file:text-xs file:cursor-pointer"
                            />
                            {slot._newPhotoName && (
                              <p className="text-xs text-yellow-400 mt-1">✓ Ready: {slot._newPhotoName}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1 text-zinc-400">Upload Video</label>
                            <input
                              type="file"
                              accept="video/mp4,video/webm"
                              id={`showcase-video-${slot.slot || index + 1}`}
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  const updated = [...showcase];
                                  updated[index] = { ...updated[index], _newVideoName: e.target.files[0].name };
                                  setShowcase(updated);
                                }
                              }}
                              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-yellow-500 file:text-black file:font-semibold file:text-xs file:cursor-pointer"
                            />
                            {slot._newVideoName && (
                              <p className="text-xs text-yellow-400 mt-1">✓ Ready: {slot._newVideoName}</p>
                            )}
                          </div>
                        </div>

                        {/* Reminder to save */}
                        {(slot._newPhotoName || slot._newVideoName) && (
                          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mb-4">
                            <p className="text-yellow-400 text-sm font-medium">
                              ⚠️ Files selected but not saved yet! Click "Save Showcase {slot.slot || index + 1}" below.
                            </p>
                          </div>
                        )}

                        {/* Save Button for this slot */}
                        <button
                          onClick={async () => {
                            setShowcaseUploading(true);
                            try {
                              const slotNum = slot.slot || index + 1;
                              const formData = new FormData();
                              formData.append('enabled', slot.enabled);
                              formData.append('caption', slot.caption || 'Click to bring this memory to life');
                              
                              const photoInput = document.getElementById(`showcase-photo-${slotNum}`);
                              const videoInput = document.getElementById(`showcase-video-${slotNum}`);
                              
                              if (photoInput?.files[0]) {
                                formData.append('photo', photoInput.files[0]);
                              }
                              formData.append('clear_photo', slot._clearPhoto ? 'true' : 'false');
                              
                              if (videoInput?.files[0]) {
                                formData.append('video', videoInput.files[0]);
                              }
                              formData.append('clear_video', slot._clearVideo ? 'true' : 'false');
                              
                              await axios.post(`${API}/admin/showcase/${slotNum}`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              // Refresh all showcase data
                              const res = await axios.get(`${API}/admin/showcase`);
                              setShowcase(res.data);
                              
                              // Clear file inputs
                              if (photoInput) photoInput.value = '';
                              if (videoInput) videoInput.value = '';
                              
                              alert(`✓ Showcase ${slotNum} saved successfully!`);
                            } catch (err) {
                              console.error('Failed to update showcase:', err);
                              alert('Failed to save. Please try again.');
                            }
                            setShowcaseUploading(false);
                          }}
                          disabled={showcaseUploading}
                          className="w-full py-3 mt-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-lg disabled:opacity-50 transition flex items-center justify-center gap-2"
                          data-testid={`save-showcase-${slot.slot || index + 1}-btn`}
                        >
                          {showcaseUploading ? (
                            <>
                              <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <Upload size={18} />
                              Save Showcase {slot.slot || index + 1}
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>

                  <p className="text-xs text-zinc-500 mt-6 text-center">
                    Enabled showcases with both photo and video will appear in the landing page carousel.
                  </p>
                </div>

                {/* Memorial Top Example Video Section */}
                <div className="card-dark p-8 bg-zinc-900/80 backdrop-blur-sm border border-yellow-500/20 mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <Sparkles className="text-yellow-500" size={20} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Top Example Video</h2>
                      <p className="text-zinc-500 text-sm">Featured video for the memorial landing page hero section</p>
                    </div>
                  </div>

                  {/* Current Video Preview */}
                  {memorialShowcase.video_url ? (
                    <div className="mb-8">
                      <p className="text-sm text-zinc-400 mb-3">Current Memorial Showcase Video</p>
                      <div className="relative max-w-2xl mx-auto">
                        <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/30 via-yellow-400/20 to-yellow-500/30 rounded-2xl blur-xl animate-pulse"></div>
                        <div className="absolute -inset-2 bg-yellow-500/10 rounded-xl blur-md"></div>
                        <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl shadow-yellow-500/20 ring-1 ring-yellow-500/30">
                          <video
                            src={`${BACKEND_URL}${memorialShowcase.video_url}`}
                            controls
                            className="w-full aspect-video"
                            data-testid="memorial-showcase-preview"
                          />
                        </div>
                      </div>
                      {memorialShowcase.caption && (
                        <p className="text-center text-zinc-400 mt-4 italic">"{memorialShowcase.caption}"</p>
                      )}
                      <div className="flex justify-center mt-6">
                        <button
                          onClick={handleDeleteMemorialShowcase}
                          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition"
                          data-testid="delete-memorial-showcase-btn"
                        >
                          <Trash2 size={16} /> Remove Video
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-8">
                      <div className="relative max-w-2xl mx-auto">
                        <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/20 via-yellow-400/10 to-yellow-500/20 rounded-2xl blur-xl"></div>
                        <div className="relative bg-zinc-800/50 rounded-xl aspect-video flex items-center justify-center border-2 border-dashed border-zinc-700">
                          <div className="text-center">
                            <Video className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                            <p className="text-zinc-500">No showcase video uploaded</p>
                            <p className="text-zinc-600 text-sm mt-1">Upload a memorial video below</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Upload Section */}
                  <div className="border-t border-zinc-700/50 pt-6 mt-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Upload size={18} className="text-yellow-500" />
                      {memorialShowcase.video_url ? "Replace Showcase Video" : "Upload Showcase Video"}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Caption (Optional)</label>
                        <input
                          type="text"
                          value={memorialShowcaseCaption}
                          onChange={(e) => setMemorialShowcaseCaption(e.target.value)}
                          placeholder="Honor your loved ones with a beautiful tribute"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500 transition"
                          data-testid="memorial-showcase-caption-input"
                        />
                      </div>
                      <div className="flex gap-4 items-center">
                        <input
                          ref={memorialShowcaseFileRef}
                          type="file"
                          accept="video/*,.mp4,.mov,.webm,.avi,.mkv,.m4v"
                          onChange={(e) => setMemorialShowcaseFile(e.target.files?.[0] || null)}
                          className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-yellow-500/20 file:text-yellow-400 file:font-semibold file:cursor-pointer hover:file:bg-yellow-500/30 transition"
                          data-testid="memorial-showcase-file-input"
                          disabled={memorialShowcaseUploading}
                        />
                        <button
                          onClick={handleUploadMemorialShowcase}
                          disabled={memorialShowcaseUploading || !memorialShowcaseFile}
                          className="bg-yellow-500 hover:bg-yellow-400 text-black px-6 py-2.5 rounded-lg font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                          data-testid="upload-memorial-showcase-btn"
                        >
                          {memorialShowcaseUploading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload size={16} /> Upload
                            </>
                          )}
                        </button>
                      </div>
                      {memorialShowcaseFile && !memorialShowcaseUploading && (
                        <p className="text-sm text-yellow-400">
                          ✓ Selected: {memorialShowcaseFile.name} ({Math.round(memorialShowcaseFile.size / (1024 * 1024))}MB)
                        </p>
                      )}
                      {memorialShowcaseUploading && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-400">Uploading video...</span>
                            <span className="text-yellow-400">{memorialShowcaseProgress}%</span>
                          </div>
                          <div className="w-full bg-zinc-700 rounded-full h-2">
                            <div 
                              className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${memorialShowcaseProgress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-yellow-400 text-sm">
                      <strong>Tip:</strong> This video will autoplay (muted) on the Memorial landing page hero section.
                    </p>
                  </div>
                </div>

                {/* Memorial Bottom Example Video Section */}
                <div className="card-dark p-8 bg-zinc-900/80 backdrop-blur-sm border border-yellow-500/20 mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <Film className="text-yellow-500" size={20} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Bottom Example Video</h2>
                      <p className="text-zinc-500 text-sm">Secondary showcase video displayed in "See What We Create" section</p>
                    </div>
                  </div>

                  {/* Current Bottom Video Preview */}
                  {memorialShowcase2.video_url ? (
                    <div className="mb-8">
                      <p className="text-sm text-zinc-400 mb-3">Current Bottom Example Video</p>
                      <div className="relative max-w-2xl mx-auto">
                        <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/30 via-yellow-400/20 to-yellow-500/30 rounded-2xl blur-xl animate-pulse"></div>
                        <div className="absolute -inset-2 bg-yellow-500/10 rounded-xl blur-md"></div>
                        <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl shadow-yellow-500/20 ring-1 ring-yellow-500/30">
                          <video
                            src={`${BACKEND_URL}${memorialShowcase2.video_url}`}
                            controls
                            className="w-full aspect-video"
                            data-testid="memorial-showcase2-preview"
                          />
                        </div>
                      </div>
                      <div className="flex justify-center mt-6">
                        <button
                          onClick={handleDeleteMemorialShowcase2}
                          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition"
                          data-testid="delete-memorial-showcase2-btn"
                        >
                          <Trash2 size={16} /> Remove Video
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-8">
                      <div className="relative max-w-2xl mx-auto">
                        <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/20 via-yellow-400/10 to-yellow-500/20 rounded-2xl blur-xl"></div>
                        <div className="relative bg-zinc-800/50 rounded-xl aspect-video flex items-center justify-center border-2 border-dashed border-zinc-700">
                          <div className="text-center">
                            <Film className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                            <p className="text-zinc-500">No bottom video uploaded</p>
                            <p className="text-zinc-600 text-sm mt-1">Upload a memorial demo video below</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Upload Section */}
                  <div className="border-t border-zinc-700/50 pt-6 mt-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Upload size={18} className="text-yellow-500" />
                      {memorialShowcase2.video_url ? "Replace Bottom Video" : "Upload Bottom Video"}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Caption (Optional)</label>
                        <input
                          type="text"
                          value={memorialShowcase2Caption}
                          onChange={(e) => setMemorialShowcase2Caption(e.target.value)}
                          placeholder="See how we transform your memories"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500 transition"
                          data-testid="memorial-showcase2-caption-input"
                        />
                      </div>
                      <div className="flex gap-4 items-center">
                        <input
                          ref={memorialShowcase2FileRef}
                          type="file"
                          accept="video/*,.mp4,.mov,.webm,.avi,.mkv,.m4v"
                          onChange={(e) => setMemorialShowcase2File(e.target.files?.[0] || null)}
                          className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-yellow-500/20 file:text-yellow-400 file:font-semibold file:cursor-pointer hover:file:bg-yellow-500/30 transition"
                          data-testid="memorial-showcase2-file-input"
                          disabled={memorialShowcase2Uploading}
                        />
                        <button
                          onClick={handleUploadMemorialShowcase2}
                          disabled={memorialShowcase2Uploading || !memorialShowcase2File}
                          className="bg-yellow-500 hover:bg-yellow-400 text-black px-6 py-2.5 rounded-lg font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                          data-testid="upload-memorial-showcase2-btn"
                        >
                          {memorialShowcase2Uploading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload size={16} /> Upload
                            </>
                          )}
                        </button>
                      </div>
                      {memorialShowcase2File && !memorialShowcase2Uploading && (
                        <p className="text-sm text-yellow-400">
                          ✓ Selected: {memorialShowcase2File.name} ({Math.round(memorialShowcase2File.size / (1024 * 1024))}MB)
                        </p>
                      )}
                      {memorialShowcase2Uploading && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-400">Uploading video...</span>
                            <span className="text-yellow-400">{memorialShowcase2Progress}%</span>
                          </div>
                          <div className="w-full bg-zinc-700 rounded-full h-2">
                            <div 
                              className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${memorialShowcase2Progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-yellow-400 text-sm">
                      <strong>Tip:</strong> This video will appear in the "See What We Create" section below the pricing cards.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Real Estate - Showcase */}
            {activeTab === "real_estate" && activeSubTab === "showcase" && (
              <REShowcaseUploader 
                reShowcase={reShowcase}
                setReShowcase={setReShowcase}
                reShowcase2={reShowcase2}
                setReShowcase2={setReShowcase2}
                API={API}
                BACKEND_URL={BACKEND_URL}
              />
            )}

            {/* Examples Tab */}
            {/* Memorial - Example Videos */}
            {activeTab === "memorial" && activeSubTab === "examples" && (
              <div className="space-y-6">
                {/* Upload Form */}
                <div className="card-dark p-6">
                  <h2 className="font-semibold mb-4">Upload Example Video</h2>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Title *</label>
                      <input
                        type="text"
                        value={exampleTitle}
                        onChange={(e) => setExampleTitle(e.target.value)}
                        placeholder="Memorial Video Example"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500"
                        data-testid="example-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <input
                        type="text"
                        value={exampleDesc}
                        onChange={(e) => setExampleDesc(e.target.value)}
                        placeholder="A beautiful tribute..."
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500"
                        data-testid="example-desc-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 items-center">
                    <input
                      ref={exampleFileInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleExampleFileSelect}
                      className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-zinc-700 file:text-white file:font-semibold file:cursor-pointer hover:file:bg-zinc-600"
                      data-testid="example-file-input"
                    />
                    <button
                      onClick={handleUploadExample}
                      disabled={uploading || !exampleTitle.trim() || !exampleFile}
                      className="btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="upload-example-btn"
                    >
                      {uploading ? "Uploading..." : "Upload Video"}
                    </button>
                  </div>
                  {exampleFile && (
                    <p className="text-sm text-green-400 mt-2">
                      ✓ Selected: {exampleFile.name}
                    </p>
                  )}
                </div>

                {/* Examples List */}
                <div className="space-y-3">
                  <h2 className="font-semibold">Current Examples ({examples.length})</h2>
                  {examples.length === 0 ? (
                    <div className="card-dark p-8 text-center text-zinc-500">
                      No example videos yet. Upload your first one above.
                    </div>
                  ) : (
                    examples.map((example, index) => (
                      <div key={example.id} className="card-dark p-4 flex items-center gap-4">
                        <div className="text-zinc-500 cursor-move">
                          <GripVertical size={20} />
                        </div>
                        <div className="w-32 h-20 bg-black rounded overflow-hidden flex-shrink-0">
                          <video src={`${BACKEND_URL}${example.url}`} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold">{example.title}</div>
                          {example.description && (
                            <div className="text-sm text-zinc-400">{example.description}</div>
                          )}
                          <div className="text-xs text-zinc-500 mt-1">Order: {index + 1}</div>
                        </div>
                        <button
                          onClick={() => handleDeleteExample(example.id)}
                          className="p-2 text-red-500 hover:bg-red-500/20 rounded"
                          data-testid={`delete-example-${example.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Video Book Tab */}
            {/* Memorial - Video Book */}
            {activeTab === "memorial" && activeSubTab === "videobook" && (
              <div className="space-y-6">
                <div className="card-dark p-6">
                  <h2 className="font-semibold mb-4 flex items-center gap-2">
                    <BookOpen size={20} className="text-yellow-500" />
                    Video Book Showcase Video
                  </h2>
                  <p className="text-zinc-400 text-sm mb-4">
                    Upload a video to showcase the Video Book product on the landing page. This video will replace the static images in the &quot;A Memorial You Can Hold&quot; section.
                  </p>
                  
                  {/* Video Book Upload */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Video File</label>
                      <div className="flex gap-4 items-center">
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setVideoBookFile(file);
                            }
                          }}
                          className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-zinc-700 file:text-white file:font-semibold file:cursor-pointer hover:file:bg-zinc-600"
                          data-testid="videobook-file-input"
                        />
                        <button
                          onClick={handleUploadVideoBook}
                          disabled={videoBookUploading || !videoBookFile}
                          className="btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="upload-videobook-btn"
                        >
                          {videoBookUploading ? "Uploading..." : "Upload Video"}
                        </button>
                      </div>
                      {videoBookFile && (
                        <p className="text-sm text-green-400 mt-2">
                          ✓ Selected: {videoBookFile.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Current Video Book Video */}
                <div className="card-dark p-6">
                  <h2 className="font-semibold mb-4">Current Video Book Video</h2>
                  {videoBookData?.video_url ? (
                    <div className="space-y-4">
                      <div className="aspect-video bg-black rounded-lg overflow-hidden max-w-md">
                        <video 
                          src={`${BACKEND_URL}${videoBookData.video_url}`} 
                          controls 
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleDeleteVideoBook}
                          className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center gap-2"
                          data-testid="delete-videobook-btn"
                        >
                          <Trash2 size={16} /> Remove Video
                        </button>
                      </div>
                      <p className="text-sm text-zinc-500">
                        Uploaded: {videoBookData.uploaded_at ? new Date(videoBookData.uploaded_at).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center text-zinc-500 py-8">
                      <BookOpen size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No video uploaded yet.</p>
                      <p className="text-sm">Upload a video above to showcase the Video Book product.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {/* Memorial - Analytics */}
            {activeTab === "memorial" && activeSubTab === "analytics" && analytics && (
              <div className="space-y-6">
                {/* Overview Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="card-dark p-4 text-center">
                    <div className="text-3xl font-bold text-yellow-500">{analytics.funnel?.cart_started || 0}</div>
                    <div className="text-sm text-zinc-400">Total Orders</div>
                  </div>
                  <div className="card-dark p-4 text-center">
                    <div className="text-3xl font-bold text-green-500">{analytics.funnel?.payment_completed || 0}</div>
                    <div className="text-sm text-zinc-400">Paid Orders</div>
                  </div>
                  <div className="card-dark p-4 text-center">
                    <div className="text-3xl font-bold text-blue-500">{analytics.funnel?.order_completed || 0}</div>
                    <div className="text-sm text-zinc-400">Completed</div>
                  </div>
                  <div className="card-dark p-4 text-center">
                    <div className="text-3xl font-bold text-purple-500">{analytics.avg_fulfillment_days?.toFixed(1) || "—"}</div>
                    <div className="text-sm text-zinc-400">Avg Days to Complete</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


export default AdminPage;
          